import { FronteggContext } from '@frontegg/rest-api';
import { AxiosAmbient } from 'src/lib';
import {
  summaryDetailsSlice,
  summaryDetailsLoading,
  summaryListSlice,
  summaryListLoading,
  summaryRegenerationLoading,
  summaryExportLoading,
  icd10SummaryLoading,
  icd10SummarySlice,
  icd10UpdatedAtSlice,
  summaryRatingSlice,
  cptCodesSummarySlice,
  cptCodesSummaryLoading,
  cptCodesUpdatedAtSlice,
  dictateSummaryLoading,
  dictateSummarySlice,
  magicEditSuggestionsSlice,
  snomedCodesSummaryLoading,
  snomedCodesSummarySlice,
  snomedCodesUpdatedAtSlice,
} from '../slices/summary';
import {
  getEncounter,
  listEncounters,
  listPinnedEncounters,
} from 'src/modules/ambient_solo/store/actions/encounter';
import { showToastSuccess } from 'src/utilities/toast';
import { showToastError } from 'src/utilities/errortoast';
import { getCurrentPractitionerSettings } from 'src/store/actions/practitioner.action';

// List summaries by desc order for an encounter
export const listSummaries = (encounter_id, encounter_phase, type) => {
  return async (dispatch) => {
    if (type === 'icd-10') {
      dispatch(icd10SummaryLoading(true));
    } else if (type === 'cpt') {
      dispatch(cptCodesSummaryLoading(true));
    } else if (type === 'dictate') {
      dispatch(dictateSummaryLoading(true));
    } else if (type === 'snomed') {
      dispatch(snomedCodesSummaryLoading(true));
    } else {
      dispatch(summaryListLoading(true));
    }

    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const params = {
      encounter_id: encounter_id,
      encounter_phase: encounter_phase,
      type: type,
    };
    const result = await AxiosAmbient.get(`/summaries`, { headers, params });
    const response = result?.data;
    if (result?.status === 200) {
      if (type === 'icd-10') {
        const firstSummary = response?.summaries?.[0];
        const summaryContent = firstSummary?.summary_json
          ? JSON.parse(firstSummary.summary_json)
          : [];
        dispatch(icd10SummarySlice(summaryContent));
        dispatch(icd10UpdatedAtSlice(firstSummary?.updated_at || ''));
      } else if (type === 'cpt') {
        const firstSummary = response?.summaries?.[0];
        const summaryContent = firstSummary?.summary_json
          ? JSON.parse(firstSummary.summary_json)
          : [];
        dispatch(cptCodesSummarySlice(summaryContent));
        dispatch(cptCodesUpdatedAtSlice(firstSummary?.updated_at || ''));
      } else if (type === 'dictate') {
        dispatch(dictateSummarySlice(response?.summaries));
      } else if (type === 'snomed') {
        const firstSummary = response?.summaries?.[0];
        const summaryContent = firstSummary?.summary_json
          ? JSON.parse(firstSummary.summary_json)
          : [];
        dispatch(snomedCodesSummarySlice(summaryContent));
        dispatch(snomedCodesUpdatedAtSlice(firstSummary?.updated_at || ''));
      } else {
        dispatch(summaryListSlice(response?.summaries));
      }
    } else {
      showToastError('Failed to load summaries for encounter');
    }

    if (type === 'icd-10') {
      dispatch(icd10SummaryLoading(false));
    } else if (type === 'cpt') {
      dispatch(cptCodesSummaryLoading(false));
    } else if (type === 'dictate') {
      dispatch(dictateSummaryLoading(false));
    } else if (type === 'snomed') {
      dispatch(snomedCodesSummaryLoading(false));
    } else {
      dispatch(summaryListLoading(false));
    }
  };
};

// Get generated summary for an encounter
export const getSummary = (summary_id) => {
  return async (dispatch) => {
    dispatch(summaryDetailsLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const result = await AxiosAmbient.get(`/summaries/${summary_id}`, {
      headers,
    });
    const response = result?.data;
    if (result?.status === 200) {
      dispatch(summaryDetailsSlice(response));
    } else if (result?.status === 404) {
      dispatch(summaryDetailsSlice(''));
    } else {
      showToastError('Failed to load summary');
    }

    dispatch(summaryDetailsLoading(false));
  };
};
export const updateSummary = (
  encounter_id,
  encounter_phase,
  summary_json,
  version,
  lastUpdatedSummaryRef,
  editableSummaryRef,
  setEditedSummary,
  setIsSaving
) => {
  return async () => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const data = {
      summary_json: summary_json,
      version: version,
    };
    const params = {
      encounter_phase: encounter_phase,
    };
    if (encounter_phase === 'dictate') {
      params.type = 'dictate';
    }
    const result = await AxiosAmbient.put(
      `/summaries/version?encounter_id=${encounter_id}`,
      data,
      { headers, params }
    );
    if (result?.status === 200) {
      const response = result?.data;
      editableSummaryRef.current.version = response?.version;
      editableSummaryRef.current.updated_at = response?.updated_at;
      lastUpdatedSummaryRef.current = response;
      setEditedSummary((prev) => ({
        ...prev,
        version: response?.version,
        updated_at: response?.updated_at,
      }));
      setIsSaving(false);
    } else {
      showToastError('Failed to update summary');
    }
  };
};

export const rollbackSummary = (
  encounter_id,
  encounter_phase,
  version,
  lastUpdatedSummaryRef,
  editableSummaryRef,
  setEditedSummary,
  setIsRollingBack
) => {
  return async () => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const params = {
      encounter_phase: encounter_phase,
      encounter_id: encounter_id,
      version: version,
    };

    try {
      const result = await AxiosAmbient.put(
        `/summaries/rollback`,
        {},
        { headers, params }
      );

      if (result?.status === 200) {
        const response = result?.data;
        editableSummaryRef.current.version = response?.version;
        editableSummaryRef.current.updated_at = response?.updated_at;
        editableSummaryRef.current.summary_json = response?.summary_json;
        lastUpdatedSummaryRef.current = response;

        setEditedSummary({
          summary_json: response?.summary_json,
          version: response?.version,
          updated_at: response?.updated_at,
        });
        setIsRollingBack(false);
      } else {
        showToastError('Failed to rollback summary');
        setIsRollingBack(false);
      }
    } catch (error) {
      console.error('Error rolling back summary:', error);
      showToastError('An error occurred while rolling back the summary');
    }
  };
};

// regenerate summary for an encounter
export const regenerateSummary = (
  encounter_id,
  encounter_phase,
  searchFilters,
  custom_instruction,
  currentTemplate,
  summaryVerbosity,
  summaryFormat,
  mode = 'update'
) => {
  return async (dispatch) => {
    dispatch(summaryRegenerationLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const data = {
      custom_instructions: custom_instruction,
      template_id: currentTemplate?.template_id,
      template_overrides_id: currentTemplate?.template_overrides_id,
      summary_verbosity: summaryVerbosity,
      summary_format: summaryFormat,
      mode: mode,
    };
    const params = {
      encounter_phase: encounter_phase,
    };
    try {
      const result = await AxiosAmbient.post(
        `/encounters/${encounter_id}/regenerate_summary`,
        data,
        { headers, params }
      );
      if (result?.status === 200) {
        showToastSuccess('The updated summary will be available shortly.');
        await dispatch(listEncounters(searchFilters, false));
        await dispatch(listPinnedEncounters());
        await dispatch(getEncounter(encounter_id));
        dispatch(getCurrentPractitionerSettings());
        dispatch(summaryRegenerationLoading(false));

        return true;
      } else {
        showToastError('Failed to regenerate note');
      }
    } catch (error) {
      console.error('Error regenerating summary:', error);
      showToastError('An error occurred while regenerating the summary.');
    }
    dispatch(summaryRegenerationLoading(false));
    return false;
  };
};

// regenerate summary for an encounter
export const exportSummaryPDF = (encounter_id, encounter_phase) => {
  return async (dispatch) => {
    dispatch(summaryExportLoading(true));
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/pdf', // Change the Accept header to 'application/pdf'
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };
    const params = {
      encounter_phase: encounter_phase,
    };
    try {
      const result = await AxiosAmbient.post(
        `/encounters/${encounter_id}/export`,
        {},
        { headers, params, responseType: 'blob' }
      ); // Add responseType: 'blob'
      const blob = new Blob([result.data], { type: 'application/pdf' }); // Create the blob directly from result.data
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'summary.pdf');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Error exporting summary:', error);
      showToastError('An error occurred while exporting the summary.');
    }
    dispatch(summaryExportLoading(false));
    return false;
  };
};

export const getSummaryRating = (encounterId) => {
  return async (dispatch) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };

      const response = await AxiosAmbient.get(
        `/summaries?encounter_id=${encounterId}`,
        { headers }
      );
      if (response?.status === 200) {
        const rating =
          response?.data?.summaries[0]?.summary_ratings === null
            ? 0
            : response?.data?.summaries[0]?.summary_ratings;
        dispatch(summaryRatingSlice(rating));
      }
    } catch (error) {
      showToastError('Error occurred');
    }
  };
};

export const summaryFeedbackAction = (summaryId, summaryRating, comment) => {
  return async (dispatch) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };
      const data = {
        summary_id: summaryId,
        score: summaryRating,
        comment: comment === undefined ? '' : comment,
      };
      const result = await AxiosAmbient.post('/summary_feedback', data, {
        headers,
      });
      if (result?.status === 200) {
        dispatch(summaryRatingSlice(summaryRating));
        showToastSuccess('Feedback submitted successfully');
      }
    } catch (error) {
      showToastError('Feedback submission failed');
    }
  };
};

export const getDictateSummary = (encounterId) => {
  return async () => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };
      const data = {
        encounter_id: encounterId,
      };
      const result = await AxiosAmbient.post('/summary/dictate', data, {
        headers,
      });
      if (result?.status === 200) {
        // console.log('result', result?.data);
      }
    } catch (error) {
      showToastError('Feedback submission failed');
    }
  };
};

export const getMagicEditSuggestions = () => {
  return async (dispatch) => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };
      const result = await AxiosAmbient.get('/magic_edit_suggestions', {
        headers,
      });
      if (result?.status === 200) {
        dispatch(magicEditSuggestionsSlice(result?.data?.suggestions));
      }
    } catch (error) {
      console.log('Error fetching magic edit suggestions:', error);
      dispatch(magicEditSuggestionsSlice({}));
    }
  };
};
